import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import type { Api } from './api.js';
import { texts, useLang, type Lang } from './i18n.js';
import { loadReleases } from './releases.js';
import { TOURS, type TourId, type TourStep } from './tours.js';
import { trapTab } from './ui.js';

/**
 * The guided tour on each page (owner 2026-10-31, thread "Föreslå guidad tur per sida"): the first time a person opens a
 * page that has a tour (tours.ts), its 2–5 steps show one at a time – the page dimmed, a yellow frame round the thing the
 * step is about, and a box with why it is there, "Skip" and "Next". On a phone the box is a sheet from the bottom, so it
 * never ends up off screen. Finished or skipped counts as seen (the API keeps it with the release entries, so on every
 * device); Klara's "Show me around" and the command palette play it again.
 *
 * One thing over the page at a time: the start-up popup ("Since you were last here") waits until the tour has run, and
 * for someone who had never seen a tour it waits for the next visit (owner: new people must not get both at once).
 * A page opened from "Show me" (?nytt=) gets no tour – the person came for one button. No tours while viewing as someone.
 */

export const TOUR_TEXT = texts(
  {
    play: 'Show me around this page',
    playHint: 'guided tour guide help show me around walkthrough intro',
    none: 'This page has no guided tour',
    howReview: 'How does reviewing work?',
    label: 'Guided tour',
    step: (n: number, of: number) => `Step ${n} of ${of}`,
    skip: 'Skip',
    next: 'Next',
    done: 'Done'
  },
  {
    play: 'Visa runt på sidan',
    playHint: 'guidad tur guide hjälp visa mig runt rundtur introduktion',
    none: 'Den här sidan har ingen guidad tur',
    howReview: 'Hur fungerar granskningen?',
    label: 'Guidad tur',
    step: (n: number, of: number) => `Steg ${n} av ${of}`,
    skip: 'Hoppa över',
    next: 'Nästa',
    done: 'Klart'
  }
);

/** At most this many steps, however many a page finds. */
export const MAX_STEPS = 5;
/** How long a page may take to show its parts (it loads its data first). */
const WAIT_MS = 5000;
/** Once two steps are found, how much longer to wait for the rest. */
const SETTLE_MS = 1000;
const PORTAL_KEY = 'kff.tours';

/* ---------- shared state: the page's tour, the start-up popup and Klara ---------- */

// 'wait': the page's tour is deciding (loading what was seen, waiting for the page); App renders the tour before the popup,
// so its effect has moved to 'wait' before the popup asks
type Phase = 'wait' | 'run' | 'idle';
let phase: Phase = 'idle';
let seen: Set<string> | null = null;
// nobody had seen a tour when the Hub was opened, and one ran: the news popup waits for the next visit
let newcomer = false;
let ranForNewcomer = false;
let overlay = false;
let current: { id: TourId; play: () => boolean } | null = null;
const subs = new Set<() => void>();
const setPhase = (p: Phase) => {
  phase = p;
  for (const f of [...subs]) f();
};

/** For tests: back to a fresh page load. */
export function resetTours(): void {
  phase = 'idle';
  seen = null;
  newcomer = false;
  ranForNewcomer = false;
  overlay = false;
  current = null;
  subs.clear();
}

/**
 * Resolves once the page's tour has run or will not run. `skipNews`: a newcomer's first tour ran in this page load, so
 * the start-up popup waits for the next visit. Never waits forever (a page without a tour component).
 */
export function tourSettled(): Promise<{ skipNews: boolean }> {
  return new Promise((resolve) => {
    let done = false;
    const check = () => {
      if (done || phase !== 'idle') return;
      done = true;
      subs.delete(check);
      resolve({ skipNews: ranForNewcomer });
    };
    subs.add(check);
    check();
    // a page that never renders the tour component must not hold the popup back
    setTimeout(() => {
      if (done || phase === 'run') return;
      done = true;
      subs.delete(check);
      resolve({ skipNews: ranForNewcomer });
    }, WAIT_MS + 3000);
  });
}

/** The start-up popup is open: no tour starts underneath it. */
export function tourOverlay(open: boolean): void {
  overlay = open;
}

/** The tour of the page on screen, if it has one (Klara offers "Show me around"). */
export const pageTour = (): TourId | null => current?.id ?? null;

/** Play the page's tour again; false when the page has none (or none of its parts are on screen). */
export const playTour = (): boolean => current?.play() ?? false;

/* ---------- finding the steps ---------- */

const visible = (el: Element): boolean => {
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0 && el.getClientRects().length > 0;
};

/** The first element on screen for a step, or null. */
export function targetOf(el: string, root: ParentNode = document): HTMLElement | null {
  for (const x of root.querySelectorAll<HTMLElement>(`[data-tour="${el}"]`)) if (visible(x)) return x;
  return null;
}

/** The steps whose element is on the page now, in the tour's order. */
export const foundSteps = (steps: TourStep[], root: ParentNode = document): TourStep[] => steps.filter((s) => targetOf(s.el, root)).slice(0, MAX_STEPS);

/** Wait for the page to show its parts: all steps found, or two found and a moment more, or WAIT_MS. */
function waitForSteps(steps: TourStep[], live: () => boolean): Promise<TourStep[]> {
  const start = Date.now();
  let twoAt = 0;
  return new Promise((resolve) => {
    const tick = () => {
      if (!live()) return resolve([]);
      const found = foundSteps(steps);
      const now = Date.now();
      if (found.length >= 2 && !twoAt) twoAt = now;
      if (found.length === Math.min(steps.length, MAX_STEPS) || (twoAt && now - twoAt >= SETTLE_MS) || now - start >= WAIT_MS) return resolve(found);
      setTimeout(tick, 250);
    };
    tick();
  });
}

/* ---------- seen ---------- */

function portalSeen(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(PORTAL_KEY) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
}

async function loadSeen(api: Api | undefined): Promise<Set<string>> {
  if (seen) return seen;
  if (api) {
    const v = await loadReleases(api);
    // no answer, or an API that does not know tours yet: show none rather than every tour on every visit
    if (!v.tours) return (seen = new Set(Object.keys(TOURS)));
    seen = new Set(v.tours);
  } else seen = portalSeen();
  newcomer = seen.size === 0;
  return seen;
}

function markSeen(id: TourId, api: Api | undefined): void {
  seen?.add(id);
  if (api) {
    void api.post('/api/releases/seen', { keys: [`tour-${id}`] }).catch(() => undefined);
    return;
  }
  try {
    localStorage.setItem(PORTAL_KEY, JSON.stringify([...portalSeen(), id]));
  } catch {
    /* private mode: the tour comes back next time */
  }
}

/* ---------- the component ---------- */

/**
 * Put once on a page (App does it for the Hub's pages): the page's tour, started the first time and played again from
 * Klara. `api` stores "seen" for the person; without it (the client portal) the browser remembers. `off`: never start
 * by itself (viewing as someone).
 */
export function PageTour(p: { id: TourId | null; api?: Api; off?: boolean; lang?: Lang }) {
  const ctx = useLang();
  const lang = p.lang ?? ctx;
  const [run, setRun] = useState<{ steps: TourStep[]; i: number } | null>(null);
  const owns = useRef(false);
  // focus goes back where it was when the tour ends
  const back = useRef<HTMLElement | null>(null);
  const begin = (steps: TourStep[]) => {
    owns.current = true;
    back.current = document.activeElement as HTMLElement | null;
    setRun({ steps, i: 0 });
    setPhase('run');
  };
  useEffect(() => {
    const id = p.id;
    let live = true;
    if (!id) {
      current = null;
      if (phase !== 'run') setPhase('idle');
      return;
    }
    const steps = TOURS[id];
    current = {
      id,
      play: () => {
        const found = foundSteps(steps);
        if (!found.length) return false;
        begin(found);
        return true;
      }
    };
    const auto = !p.off && !new URLSearchParams(location.search).get('nytt');
    if (!auto) {
      if (phase !== 'run') setPhase('idle');
    } else {
      setPhase('wait');
      void (async () => {
        const s = await loadSeen(p.api).catch(() => null);
        if (!live) return;
        if (!s || s.has(id) || overlay) return setPhase('idle');
        const found = await waitForSteps(steps, () => live);
        if (!live) return;
        if (found.length < 2 || overlay) return setPhase('idle');
        if (newcomer) ranForNewcomer = true;
        begin(found);
      })();
    }
    return () => {
      live = false;
      if (current?.id === id) current = null;
      if (owns.current) {
        owns.current = false;
        setPhase('idle');
      }
    };
  }, [p.id]);
  if (!run || !p.id) return null;
  const end = () => {
    markSeen(p.id!, p.api);
    owns.current = false;
    setRun(null);
    setPhase('idle');
    back.current?.focus?.({ preventScroll: true });
  };
  return <TourBox lang={lang} steps={run.steps} i={run.i} next={() => (run.i + 1 < run.steps.length ? setRun({ ...run, i: run.i + 1 }) : end())} skip={end} />;
}

/* ---------- the box ---------- */

const PAD = 6;
const GAP = 14;
const POP_W = 340;
const EDGE = 16;
const sheetMedia = '(max-width: 720px)';

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

const topbar = () => document.querySelector('.topbar')?.getBoundingClientRect().bottom ?? 0;

/** Where the box goes beside the frame: right, left, below, above, or the corner when the thing fills the screen. */
export function placePop(t: Box, pop: { w: number; h: number }, view: { w: number; h: number; top: number }): { left: number; top: number } {
  const clampX = (x: number) => Math.min(Math.max(x, EDGE), view.w - pop.w - EDGE);
  const clampY = (y: number) => Math.min(Math.max(y, view.top + EDGE), view.h - pop.h - EDGE);
  if (t.x + t.w + GAP + pop.w <= view.w - EDGE) return { left: t.x + t.w + GAP, top: clampY(t.y) };
  if (t.x - GAP - pop.w >= EDGE) return { left: t.x - GAP - pop.w, top: clampY(t.y) };
  if (t.y + t.h + GAP + pop.h <= view.h - EDGE) return { left: clampX(t.x), top: t.y + t.h + GAP };
  if (t.y - GAP - pop.h >= view.top + EDGE) return { left: clampX(t.x), top: t.y - GAP - pop.h };
  return { left: view.w - pop.w - EDGE * 1.5, top: view.h - pop.h - EDGE * 1.5 };
}

function TourBox(p: { lang: Lang; steps: TourStep[]; i: number; next: () => void; skip: () => void }) {
  const t = TOUR_TEXT[p.lang];
  const step = p.steps[p.i]!;
  const [title, text] = step[p.lang];
  const last = p.i === p.steps.length - 1;
  const [rect, setRect] = useState<Box | null>(null);
  const [popH, setPopH] = useState(180);
  const [sheet, setSheet] = useState(() => matchMedia(sheetMedia).matches);
  const pop = useRef<HTMLDivElement>(null);
  const nextBtn = useRef<HTMLButtonElement>(null);

  // bring the thing into view (above the sheet on a phone), then follow it while the page moves
  useEffect(() => {
    const el = targetOf(step.el);
    if (!el) return p.next();
    const r = el.getBoundingClientRect();
    const top = topbar() + 12;
    const bottom = innerHeight - (matchMedia(sheetMedia).matches ? (pop.current?.offsetHeight ?? 220) + 12 : 12);
    if (r.top < top || r.bottom > bottom) {
      const room = bottom - top;
      const y = r.height <= room ? r.top - top - (room - r.height) / 2 : r.top - top;
      scrollTo({ top: scrollY + y, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
    let raf = 0;
    let was = '';
    const follow = () => {
      const b = el.getBoundingClientRect();
      const now = `${b.left}|${b.top}|${b.width}|${b.height}`;
      if (now !== was) {
        was = now;
        setRect({ x: b.left, y: b.top, w: b.width, h: b.height });
      }
      raf = requestAnimationFrame(follow);
    };
    follow();
    const media = matchMedia(sheetMedia);
    const onMedia = () => setSheet(media.matches);
    media.addEventListener('change', onMedia);
    return () => {
      cancelAnimationFrame(raf);
      media.removeEventListener('change', onMedia);
    };
  }, [step.el]);

  useLayoutEffect(() => {
    if (pop.current) setPopH(pop.current.offsetHeight);
  });

  // the button in focus on every step; Escape skips
  useEffect(() => {
    nextBtn.current?.focus({ preventScroll: true });
  }, [p.i]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      p.skip();
    };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, [p.skip]);

  const hole = rect ? { left: rect.x - PAD, top: rect.y - PAD, width: rect.w + PAD * 2, height: rect.h + PAD * 2 } : null;
  const at = rect && !sheet ? placePop({ x: rect.x - PAD, y: rect.y - PAD, w: rect.w + PAD * 2, h: rect.h + PAD * 2 }, { w: POP_W, h: popH }, { w: innerWidth, h: innerHeight, top: topbar() }) : null;
  return (
    <div class="tour" data-step={step.el}>
      {/* the page stays still under the tour: a click outside the box does nothing */}
      <div class="tour-block" onClick={(e) => e.stopPropagation()} />
      {hole ? <div class="tour-hole" style={{ left: `${hole.left}px`, top: `${hole.top}px`, width: `${hole.width}px`, height: `${hole.height}px` }} aria-hidden="true" /> : <div class="tour-dim" aria-hidden="true" />}
      <div
        ref={pop}
        class={`tour-pop${sheet ? ' tour-pop--sheet' : ''}`}
        style={at ? { left: `${at.left}px`, top: `${at.top}px` } : undefined}
        role="dialog"
        aria-modal="true"
        aria-label={t.label}
        aria-describedby="tour-text"
        onKeyDown={(e) => pop.current && trapTab(pop.current, e)}
      >
        <div class="tour-meta">
          <span>{t.step(p.i + 1, p.steps.length)}</span>
          <span class="tour-dots" aria-hidden="true">
            {p.steps.map((_, n) => (
              <i class={n === p.i ? 'is-on' : undefined} />
            ))}
          </span>
        </div>
        <h2 class="tour-h">{title}</h2>
        <p class="tour-text" id="tour-text">
          {text}
        </p>
        <div class="tour-acts">
          {last ? (
            <span />
          ) : (
            <button type="button" class="linkish tour-skip" onClick={p.skip}>
              {t.skip}
            </button>
          )}
          <button type="button" class="btn btn-primary" ref={nextBtn} onClick={p.next}>
            {last ? t.done : t.next}
          </button>
        </div>
      </div>
    </div>
  );
}
